import { useCallback, useMemo } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";

interface UseTimePickerParams {
  // interval in minutes
  steps: number;
  value: Date | undefined;
  onChange: (value: Date) => void;
}

export function useTimePicker({ steps, value, onChange }: UseTimePickerParams) {
  const { formatDateTime, DateTimeFormatStyle } = useI18nContext();
  const timeFormat = DateTimeFormatStyle.Custom({
    hour: "2-digit",
    minute: "2-digit",
  });
  const options = useMemo(() => {
    const options = [] as { label: string; time: Date; value: string }[];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += steps) {
        const time = new Date(0, 0, 0, h, m);
        options.push({
          label: formatDateTime(time, timeFormat),
          time,
          value: time.toISOString(),
        });
      }
    }
    return options;
  }, [steps, formatDateTime, timeFormat]);

  const hours = value?.getHours() ?? 0;
  const minutes = value?.getMinutes() ?? 0;
  const onSelectChange = useCallback(
    (timeStr: string) => {
      if (!value) {
        return;
      }
      const modified = new Date(value);
      const time = new Date(timeStr);
      modified.setHours(time.getHours(), time.getMinutes(), 0, 0);
      onChange(modified);
    },
    [value, onChange]
  );

  const selected = value
    ? options.find(
        (option) =>
          option.time.getHours() > hours ||
          (option.time.getHours() === hours &&
            option.time.getMinutes() >= minutes)
      )
    : undefined;

  return {
    hours,
    minutes,
    options,
    onSelectChange,
    selectedOption: selected?.value,
  };
}
